/**
 * HeroIslands — 3 floating "island" tiles that orbit the central King.
 *
 * Layout (per approved concept render, Aug 2026):
 *   ┌────────────────────────────────────────────────┐
 *   │  ┌───────┐          ┌───────┐    ┌───────┐     │
 *   │  │ FISH  │          │ KING  │    │ WHALE │     │
 *   │  │ TABLE │          │  🎁   │    │ LOUNGE│     │
 *   │  └───────┘          └───────┘    └───────┘     │
 *   │                     ┌───────┐                  │
 *   │                     │ SLOTS │                  │
 *   │                     └───────┘                  │
 *   └────────────────────────────────────────────────┘
 *
 * - Desktop  (md+):  CSS Grid, King centered, Fish left, Whale right,
 *                    Slots below King (visually anchoring the group).
 * - Mobile   (<md):  Single-column stack — King → Fish → Slots → Whale.
 *                    Keeps the header clean for iPhone 15+ / Samsung.
 *
 * Each island button has:
 *   • Floating animation (translateY, staggered phase per island).
 *   • On-hover gold ring + scale.
 *   • Click handler → smooth scroll to the requested anchor element,
 *     or fall-through navigate if we're on a page without the anchor.
 *
 * Props:
 *   renderKing — function ⇒ ReactNode. Returned inside the grid so the
 *     King's click semantics (guest → signup, user → /daily-wheel) stay
 *     100% owned by the parent.
 */
import Mascot from "./Mascot";

const scrollToAnchor = (id, fallbackHref, navigate) => () => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (fallbackHref && navigate) {
    navigate(fallbackHref);
  }
};

export default function HeroIslands({ renderKing, navigate }) {
  return (
    <div
      className="jr-hero-scene relative w-full max-w-5xl mx-auto"
      data-testid="hero-islands-scene"
    >
      <div className="jr-hero-grid">
        {/* ── FISH TABLES ── (left, floating phase 0) */}
        <button
          type="button"
          onClick={scrollToAnchor("popular-now", "/lobby?category=fish", navigate)}
          className="jr-island jr-island-fish group"
          data-testid="hero-island-fish"
        >
          <Mascot slug="egyptian_princess" size="md" role="inline" tip="Reel in the fish 🐠" className="!w-16 !h-16 md:!w-20 md:!h-20" />
          <div className="text-left">
            <p className="text-[9px] font-black tracking-[0.18em] text-cyan-300">CATEGORY</p>
            <p className="text-sm md:text-base font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Fish Tables
            </p>
            <p className="text-[10px] text-cyan-200/80">Shoot · Win · Cash</p>
          </div>
        </button>

        {/* ── KING (center, unchanged CTA) ── */}
        <div className="jr-island-king">
          {renderKing()}
        </div>

        {/* ── WHALE LOUNGE ── (right, floating phase 2) */}
        <button
          type="button"
          onClick={scrollToAnchor("games", "/signup", navigate)}
          className="jr-island jr-island-whale group"
          data-testid="hero-island-whale"
        >
          <Mascot slug="mafia_boss" size="md" role="inline" tip="Whales only ♠️" className="!w-16 !h-16 md:!w-20 md:!h-20" />
          <div className="text-left">
            <p className="text-[9px] font-black tracking-[0.18em] text-purple-300">VIP ONLY</p>
            <p className="text-sm md:text-base font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Whale Lounge
            </p>
            <p className="text-[10px] text-purple-200/80">Elite · High-stakes</p>
          </div>
        </button>

        {/* ── SLOTS ── (center-below, floating phase 1) */}
        <button
          type="button"
          onClick={scrollToAnchor("games", "/lobby", navigate)}
          className="jr-island jr-island-slots group"
          data-testid="hero-island-slots"
        >
          <Mascot slug="viking_warrior" size="md" role="inline" tip="Reels on fire ⚔️" className="!w-16 !h-16 md:!w-20 md:!h-20" />
          <div className="text-left">
            <p className="text-[9px] font-black tracking-[0.18em] text-amber-300">FEATURED</p>
            <p className="text-sm md:text-base font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              Slots
            </p>
            <p className="text-[10px] text-amber-200/80">2,500+ Titles</p>
          </div>
        </button>
      </div>

      <style>{`
        /* ═══ Hero Scene Grid ═══ */
        .jr-hero-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          align-items: center;
          justify-items: center;
        }
        @media (min-width: 768px) {
          .jr-hero-grid {
            grid-template-columns: 1fr auto 1fr;
            grid-template-rows: auto auto;
            row-gap: 1.5rem;
            column-gap: 2.5rem;
            justify-items: stretch;
            align-items: center;
          }
          .jr-island-fish  { grid-column: 1; grid-row: 1 / span 2; justify-self: end;   }
          .jr-island-king  { grid-column: 2; grid-row: 1; justify-self: center; }
          .jr-island-whale { grid-column: 3; grid-row: 1 / span 2; justify-self: start; }
          .jr-island-slots { grid-column: 2; grid-row: 2; justify-self: center; }
        }

        /* ═══ Island tile styling ═══ */
        .jr-island {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 215, 0, 0.25);
          background: linear-gradient(135deg, rgba(26,26,26,0.7), rgba(10,10,10,0.85));
          backdrop-filter: blur(8px);
          box-shadow: 0 15px 40px -10px rgba(0,0,0,0.6),
                      inset 0 1px 0 rgba(255,215,0,0.15);
          cursor: pointer;
          transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
          text-align: left;
          will-change: transform;
        }
        .jr-island:hover {
          transform: translateY(-3px) scale(1.02);
          border-color: rgba(255, 215, 0, 0.6);
          box-shadow: 0 20px 55px -8px rgba(255,215,0,0.35),
                      inset 0 1px 0 rgba(255,215,0,0.3);
        }
        .jr-island:active { transform: scale(0.97); }

        /* Per-island accent border */
        .jr-island-fish  { border-color: rgba(34,211,238,0.35); }
        .jr-island-fish:hover  { border-color: rgba(34,211,238,0.85); box-shadow: 0 20px 55px -8px rgba(34,211,238,0.35); }
        .jr-island-whale { border-color: rgba(168,85,247,0.35); }
        .jr-island-whale:hover { border-color: rgba(168,85,247,0.85); box-shadow: 0 20px 55px -8px rgba(168,85,247,0.35); }
        .jr-island-slots { border-color: rgba(251,191,36,0.35); }
        .jr-island-slots:hover { border-color: rgba(251,191,36,0.85); box-shadow: 0 20px 55px -8px rgba(251,191,36,0.35); }

        /* ═══ Slow, staggered floating animation ═══ */
        @keyframes jr-island-float-a {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes jr-island-float-b {
          0%, 100% { transform: translateY(-4px); }
          50%      { transform: translateY(6px); }
        }
        @keyframes jr-island-float-c {
          0%, 100% { transform: translateY(4px); }
          50%      { transform: translateY(-8px); }
        }
        .jr-island-fish  { animation: jr-island-float-a 5.5s ease-in-out infinite; }
        .jr-island-whale { animation: jr-island-float-b 6.2s ease-in-out infinite; }
        .jr-island-slots { animation: jr-island-float-c 5.0s ease-in-out infinite; }
        /* Hover disables the float so the transform above lands cleanly */
        .jr-island:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .jr-island-fish, .jr-island-whale, .jr-island-slots { animation: none; }
        }
      `}</style>
    </div>
  );
}
